import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useDateFormat } from '@/composables/dateFormat';
import { ActivityBudgetAction, ActivityBudgetGetter } from '@/store/activityBudget/activityBudget.module';
import { ActivityBudget } from '@/store/activityBudget/activityBudget.model';
import LoadingComponent from '../loading/Loading.vue';
import ActivityBudgetFormComponent from './activityBudgetForm/ActivityBudgetForm.vue';
import ActivityBudgetOffcanvasComponent from './activityBudgetOffcanvas/ActivityBudgetOffcanvas.vue';

export default defineComponent({
  name: 'ActivityBudgetTableComponent',
  components: {
    LoadingComponent,
    ActivityBudgetFormComponent,
    ActivityBudgetOffcanvasComponent
  },
  props: {
    activityId: {
      type: Number,
      required: true,
    },
    provinceId: {
      type: Number,
      required: true,
    }
  },
  setup: (props) => {
    const store = useStore();
    const dateFormat = useDateFormat();
    const activityBudgets = computed<ActivityBudget[]>(() => store.getters[ActivityBudgetGetter.ALL]);
    const isLoading = computed(() => store.getters[ActivityBudgetGetter.STATUS] === 'init' || store.getters[ActivityBudgetGetter.STATUS] === 'loading');
    const datatable = ref<HTMLTableElement>();
    const dtInstance = ref();
    const activityBudgetOffcanvas = ref<InstanceType<typeof ActivityBudgetOffcanvasComponent>>();
    const activityBudgetForm = ref<InstanceType<typeof ActivityBudgetFormComponent>>();
    const activityBudgetId = ref<number | null>(null);

    onMounted(() => {
      updateDataTable(props.activityId, props.provinceId);
    });

    watch(() => ([props.activityId, props.provinceId]), ([activityId, provinceId]) => {
      updateDataTable(activityId, provinceId);
    });

    onUnmounted(() => {
      dtInstance.value?.destroy();
    });

    const updateDataTable = (activityId: number, provinceId: number) => {
      Promise.resolve(dtInstance.value?.destroy())
        .then(() => {
          store.dispatch(ActivityBudgetAction.FETCH, { activityId: activityId, provinceId: provinceId })
            .then(() => initialDataTable());
        });
    };

    const initialDataTable = () => {
      const dt = datatable.value as HTMLTableElement;
      if (!$.fn.dataTable.isDataTable(dt)) {
        dtInstance.value = $(dt).DataTable({
          language: {
            emptyTable: 'ไม่พบข้อมูล',
            info: 'แสดง _START_ ถึง _END_ จากจำนวนทั้งหมด _TOTAL_',
            infoEmpty: 'แสดง 0 ถึง 0 จากจำนวนทั้งหมด 0',
            infoFiltered: '(ค้นหาจากจำนวนทั้งหมด _MAX_)',
            lengthMenu: 'แสดงข้อมูล _MENU_',
            paginate: {
              next: 'ถัดไป',
              previous: 'ก่อนหน้า',
              first: 'First',
              last: 'Last'
            },
            search: 'ค้นหา:',
            zeroRecords: 'ไม่พบข้อมูลที่ค้นหา',
          }
        });
      }
    };

    const createClick = () => {
      Promise.resolve(activityBudgetId.value = null)
        .then(() => activityBudgetForm.value?.show());
    };

    const selectedClick = (id: number) => {
      Promise.resolve(activityBudgetId.value = id)
        .then(() => activityBudgetOffcanvas.value?.show());
    };

    const editClick = (id: number) => {
      Promise.resolve(activityBudgetId.value = id)
        .then(() => activityBudgetForm.value?.show());
    };

    const thaiDate = (date: Date) =>
      dateFormat.toThaiDate(date, 'short');

    return {
      activityBudgets,
      isLoading,
      datatable,
      activityBudgetOffcanvas,
      activityBudgetForm,
      activityBudgetId,
      createClick,
      selectedClick,
      editClick,
      initialDataTable,
      thaiDate,
    };
  },
});
